import { useEffect, useRef, useState } from "react";
import { Upload, FileArchive, CheckCircle2, AlertTriangle, RefreshCw, Building2, Loader2, FileText, Users, Sparkles } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { api } from "./adminApi";

const STATUS_LABEL = {
  queued: "En attente",
  extracting: "Décompression",
  processing: "Traitement en cours",
  done: "Terminé",
  failed: "Échec",
};
const STATUS_COLOR = {
  queued: "bg-gray-100 text-gray-700",
  extracting: "bg-blue-100 text-blue-700",
  processing: "bg-amber-100 text-amber-800",
  done: "bg-green-100 text-green-800",
  failed: "bg-red-100 text-red-700",
};

const DOC_TYPE_LABEL = {
  cv: "CV", id_card: "Pièce d'identité", carte_sejour: "Carte de séjour",
  attestation_secu: "Attestation Sécu", justificatif_domicile: "Justif. domicile",
  certificat_travail: "Certif. travail / bulletins", visite_medicale: "Visite médicale",
  rqth: "RQTH", caces_habilitations: "CACES / habilitations", rib: "RIB", other: "Autre",
};

export default function BulkImportTab() {
  const [agencies, setAgencies] = useState([]);
  const [agencyId, setAgencyId] = useState("");
  const [file, setFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [uploadPct, setUploadPct] = useState(0);
  const [activeJob, setActiveJob] = useState(null);
  const [history, setHistory] = useState([]);
  const [showDetails, setShowDetails] = useState(false);
  const [stuckCount, setStuckCount] = useState(0);
  const [resuming, setResuming] = useState(false);
  const inputRef = useRef(null);
  const pollRef = useRef(null);

  const refreshHistory = async () => {
    try { const r = await api.get("/admin/bulk-import"); setHistory(r.data || []); }
    catch { /* silent */ }
  };

  const refreshStuck = async () => {
    try {
      const r = await api.get("/admin/ai-extract/stuck");
      setStuckCount(r.data?.count || 0);
    } catch { /* silent */ }
  };

  const handleResumeStuck = async () => {
    setResuming(true);
    try {
      const r = await api.post("/admin/ai-extract/resume-stuck");
      const msg = r.data?.message || "Relance lancée";
      toast.success(msg, { duration: 6000 });
      // Refresh count after a short delay (jobs run in background)
      setTimeout(refreshStuck, 6000);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erreur de relance");
    } finally {
      setResuming(false);
    }
  };

  const handleResetRetries = async () => {
    if (!window.confirm("Remettre en file d'attente tous les candidats en timeout/erreur ? Ils seront ré-analysés au prochain cycle.")) return;
    try {
      const r = await api.post("/admin/ai-extract/reset-retries");
      toast.success(r.data?.message || "Reset effectué");
      setTimeout(refreshStuck, 1500);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erreur de reset");
    }
  };

  useEffect(() => {
    (async () => {
      try { const a = await api.get("/agencies"); setAgencies(a.data || []); } catch {}
    })();
    refreshHistory();
    refreshStuck();
    // Auto-refresh stuck count every 30s (in case the background scheduler picks them up silently)
    const t = setInterval(refreshStuck, 30000);
    return () => { if (pollRef.current) clearInterval(pollRef.current); clearInterval(t); };
  }, []);

  const startPolling = (jobId) => {
    if (pollRef.current) clearInterval(pollRef.current);
    pollRef.current = setInterval(async () => {
      try {
        const r = await api.get(`/admin/bulk-import/${jobId}`);
        setActiveJob(r.data);
        if (r.data.status === "done" || r.data.status === "failed") {
          clearInterval(pollRef.current);
          pollRef.current = null;
          refreshHistory();
          if (r.data.status === "done") {
            toast.success(`Import terminé — ${r.data.summary?.employees_created || 0} salarié(s), ${r.data.summary?.documents_uploaded || 0} document(s)`);
          } else {
            toast.error(`Import échoué : ${r.data.error || "voir les détails"}`);
          }
        }
      } catch { /* silent — keep polling */ }
    }, 2000);
  };

  const handleUpload = async () => {
    if (!file) { toast.error("Sélectionnez un fichier .zip"); return; }
    if (!file.name.toLowerCase().endsWith(".zip")) { toast.error("Le fichier doit être un .zip"); return; }
    setUploading(true);
    setUploadPct(0);
    const fd = new FormData();
    fd.append("file", file);
    if (agencyId) fd.append("agency_id", agencyId);
    try {
      const r = await api.post("/admin/bulk-import", fd, {
        headers: { "Content-Type": "multipart/form-data" },
        onUploadProgress: (e) => {
          if (e.total) setUploadPct(Math.round((e.loaded / e.total) * 100));
        },
        timeout: 0, // ne pas timeouter le gros upload
      });
      toast.success("Archive reçue, traitement en cours…");
      setActiveJob({ job_id: r.data.job_id, status: "queued", progress: 0, summary: null });
      startPolling(r.data.job_id);
      setFile(null);
      if (inputRef.current) inputRef.current.value = "";
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erreur d'upload (le fichier est-il trop volumineux ?)");
    } finally {
      setUploading(false);
    }
  };

  const resumeJob = (jobId) => {
    setShowDetails(true);
    api.get(`/admin/bulk-import/${jobId}`).then((r) => {
      setActiveJob(r.data);
      if (r.data.status === "processing" || r.data.status === "extracting" || r.data.status === "queued") {
        startPolling(jobId);
      }
    });
  };

  const summary = activeJob?.summary || {};
  const details = summary.details || [];

  return (
    <div data-testid="bulk-import-tab" className="space-y-6">
      <div>
        <h3 className="text-lg font-bold text-[#3F3F3F] mb-1">Import massif de salariés</h3>
        <p className="text-sm text-gray-500 max-w-2xl">
          Téléversez une archive <strong>.zip</strong> contenant un dossier par salarié au format <code className="bg-gray-100 px-1">NOM Prénom/</code>.
          Chaque fichier sera attaché au salarié et son type sera détecté automatiquement (CV, CNI, RIB, carte vitale, visite médicale, bulletin de salaire…).
        </p>
      </div>

      {/* Stuck AI extractions banner — auto-recover after backend restarts */}
      {stuckCount > 0 && (
        <div className="bg-amber-50 border-2 border-amber-300 p-4 flex items-center gap-3 flex-wrap" data-testid="ai-stuck-banner">
          <Sparkles className="w-5 h-5 text-amber-600 shrink-0" />
          <div className="flex-1 min-w-0">
            <p className="text-sm font-bold text-amber-900">
              {stuckCount} analyse(s) IA bloquée(s)
            </p>
            <p className="text-xs text-amber-800">
              Ces candidats sont restés en file d'attente. Pour préserver la stabilité du serveur, ils sont traités <strong>par petits lots de 5</strong> toutes les 5 minutes. Cliquez ci-dessous pour traiter le prochain lot immédiatement.
            </p>
          </div>
          <Button
            onClick={handleResumeStuck}
            disabled={resuming}
            className="bg-amber-600 hover:bg-amber-700 text-white rounded-none text-xs h-9"
            data-testid="resume-stuck-btn"
          >
            {resuming ? <><Loader2 className="w-3 h-3 mr-1 animate-spin" /> Relance…</> : <><RefreshCw className="w-3 h-3 mr-1" /> Relancer maintenant</>}
          </Button>
          <Button
            onClick={handleResetRetries}
            variant="outline"
            className="rounded-none text-xs h-9 border-amber-400 text-amber-700 hover:bg-amber-100"
            data-testid="reset-retries-btn"
            title="Remettre en file les candidats en timeout/erreur (utile après une mise à jour)"
          >
            Reset des erreurs
          </Button>
        </div>
      )}

      {/* Upload card */}
      <div className="bg-white border border-gray-200 p-5 space-y-4">
        <div className="grid sm:grid-cols-2 gap-4">
          <div>
            <Label className="text-xs font-semibold text-gray-700 mb-1 block">Archive .zip *</Label>
            <input
              ref={inputRef}
              type="file"
              accept=".zip,application/zip,application/x-zip-compressed"
              onChange={(e) => setFile(e.target.files?.[0] || null)}
              className="block w-full text-xs file:mr-3 file:py-2 file:px-4 file:border-0 file:bg-[#F0E9F3] file:text-[#662483] file:font-semibold file:cursor-pointer hover:file:bg-[#662483] hover:file:text-white"
              data-testid="bulk-import-file"
            />
            {file && (
              <p className="text-[11px] text-gray-500 mt-1">
                <FileArchive className="inline w-3 h-3 mr-1" />
                <strong>{file.name}</strong> ({(file.size / 1024 / 1024).toFixed(1)} Mo)
              </p>
            )}
          </div>
          <div>
            <Label className="text-xs font-semibold text-gray-700 mb-1 block">
              <Building2 className="inline w-3 h-3 mr-1" /> Agence de rattachement (optionnel)
            </Label>
            <Select value={agencyId} onValueChange={setAgencyId}>
              <SelectTrigger className="rounded-none text-sm h-9" data-testid="bulk-import-agency">
                <SelectValue placeholder="Aucune (à compléter plus tard)" />
              </SelectTrigger>
              <SelectContent>
                {agencies.map((a) => (
                  <SelectItem key={a.agency_id} value={a.agency_id}>{a.name} — {a.city}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>

        <div className="bg-[#FAFAFA] border border-gray-100 p-3 text-[11px] text-gray-600 space-y-1">
          <p className="font-semibold text-[#662483]">Bon à savoir</p>
          <p>• Les comptes sont créés en rôle <strong>Salarié</strong> avec un email placeholder (<code>prenom.nom@a-completer.frinterim.fr</code>) — modifiez-le dans la fiche candidat ensuite.</p>
          <p>• Si un salarié existe déjà (créé via un import précédent), il est réutilisé (ses documents ne sont pas dupliqués au niveau du dossier mais ré-uploadés).</p>
          <p>• Format de dossier reconnu : <code>NOM Prénom</code> (le nom en MAJUSCULES) ou <code>Prénom NOM</code>.</p>
          <p>• Fichiers acceptés : PDF, JPG/PNG/WEBP, HEIC/HEIF, DOC/DOCX, XLS/XLSX — 25 Mo max par fichier.</p>
          <p>• ⚠️ Si l'upload échoue (archive {">"}~100 Mo), découpez en plusieurs ZIPs plus petits.</p>
        </div>

        <div className="flex items-center gap-3">
          <Button
            onClick={handleUpload}
            disabled={!file || uploading || (activeJob && ["queued","extracting","processing"].includes(activeJob.status))}
            className="bg-gradient-to-r from-[#662483] to-[#0069B4] text-white rounded-none font-semibold"
            data-testid="bulk-import-submit"
          >
            {uploading ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Envoi… {uploadPct}%</> : <><Upload className="w-4 h-4 mr-2" /> Lancer l'import</>}
          </Button>
          {uploading && (
            <div className="flex-1 h-2 bg-gray-100 max-w-md">
              <div className="h-full bg-[#662483] transition-all" style={{ width: `${uploadPct}%` }} />
            </div>
          )}
        </div>
      </div>

      {/* Active job progress */}
      {activeJob && (
        <div className="bg-white border-2 border-[#662483]/30 p-5 space-y-3" data-testid="bulk-import-progress">
          <div className="flex items-center gap-3 flex-wrap">
            <h4 className="text-sm font-bold text-[#662483]">Job actif</h4>
            <code className="text-[10px] bg-gray-100 px-1.5 py-0.5">{activeJob.job_id}</code>
            <Badge className={`rounded-none text-xs ${STATUS_COLOR[activeJob.status] || "bg-gray-100"}`}>
              {STATUS_LABEL[activeJob.status] || activeJob.status}
            </Badge>
            <span className="text-xs text-gray-500 ml-auto">
              Démarré par {activeJob.started_by} · {new Date(activeJob.started_at).toLocaleString("fr-FR")}
            </span>
          </div>
          {(activeJob.status === "processing" || activeJob.status === "extracting" || activeJob.status === "queued") && (
            <div className="h-3 bg-gray-100 relative overflow-hidden">
              <div className="absolute inset-y-0 left-0 bg-gradient-to-r from-[#662483] to-[#0069B4] transition-all" style={{ width: `${activeJob.progress || 0}%` }} />
              <span className="absolute inset-0 flex items-center justify-center text-[10px] font-bold text-white mix-blend-difference">
                {activeJob.progress || 0}%
              </span>
            </div>
          )}
          {summary && (
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 pt-2">
              <Stat icon={Users} label="Salariés créés" value={summary.employees_created || 0} color="text-[#662483]" />
              <Stat icon={RefreshCw} label="Salariés réutilisés" value={summary.employees_skipped || 0} color="text-blue-700" />
              <Stat icon={FileText} label="Documents OK" value={summary.documents_uploaded || 0} color="text-green-700" />
              <Stat icon={AlertTriangle} label="Documents en erreur" value={summary.documents_failed || 0} color="text-red-600" />
            </div>
          )}

          {activeJob.status === "failed" && activeJob.error && (
            <div className="bg-red-50 border border-red-200 p-3 text-xs text-red-700">
              <strong>Erreur :</strong> {activeJob.error}
            </div>
          )}

          {details.length > 0 && (
            <>
              <button
                type="button"
                onClick={() => setShowDetails((v) => !v)}
                className="text-xs text-[#662483] hover:underline"
                data-testid="bulk-import-toggle-details"
              >
                {showDetails ? "Masquer le détail par salarié" : `Voir le détail (${details.length} dossier${details.length > 1 ? "s" : ""})`}
              </button>
              {showDetails && (
                <div className="border border-gray-100 max-h-96 overflow-y-auto divide-y divide-gray-100">
                  {details.map((d, idx) => (
                    <div key={idx} className="p-3 text-xs">
                      <div className="flex items-center gap-2 flex-wrap mb-1">
                        <span className="font-bold text-[#3F3F3F]">{d.first_name} {d.last_name}</span>
                        <code className="text-[10px] bg-gray-50 px-1">{d.email || "—"}</code>
                        {d.reused_existing && <Badge className="rounded-none text-[10px] bg-blue-100 text-blue-700">Réutilisé</Badge>}
                        <Badge className={`rounded-none text-[10px] ${d.errors?.length ? "bg-red-100 text-red-700" : "bg-green-100 text-green-700"}`}>
                          {d.files?.length || 0} doc(s){d.errors?.length ? ` · ${d.errors.length} erreur(s)` : ""}
                        </Badge>
                      </div>
                      <div className="flex flex-wrap gap-1 mb-1">
                        {(d.files || []).map((f, i) => (
                          <Badge key={i} className="rounded-none text-[10px] bg-[#F0E9F3] text-[#662483]" title={f.filename}>
                            {DOC_TYPE_LABEL[f.doc_type] || f.doc_type} <span className="text-gray-500 ml-1">{f.filename.length > 30 ? f.filename.slice(0, 27) + "…" : f.filename}</span>
                          </Badge>
                        ))}
                      </div>
                      {d.errors?.length > 0 && (
                        <ul className="text-[10px] text-red-600 mt-1 list-disc list-inside">
                          {d.errors.map((e, i) => <li key={i}>{e.filename}: {e.error}</li>)}
                        </ul>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </>
          )}
        </div>
      )}

      {/* History */}
      <div className="bg-white border border-gray-200 p-5" data-testid="bulk-import-history">
        <div className="flex items-center justify-between mb-3">
          <h4 className="text-sm font-bold text-[#3F3F3F]">Imports précédents</h4>
          <Button variant="outline" size="sm" onClick={refreshHistory} className="rounded-none h-7 text-xs">
            <RefreshCw className="w-3 h-3 mr-1" /> Rafraîchir
          </Button>
        </div>
        {history.length === 0 ? (
          <p className="text-xs text-gray-500 italic py-4">Aucun import effectué pour le moment.</p>
        ) : (
          <div className="divide-y divide-gray-100">
            {history.map((j) => (
              <button
                key={j.job_id}
                type="button"
                onClick={() => resumeJob(j.job_id)}
                className="w-full flex items-center gap-3 py-2 px-1 -mx-1 hover:bg-[#FAFAFA] transition-colors text-left"
                data-testid={`bulk-import-job-${j.job_id}`}
              >
                <FileArchive className="w-4 h-4 text-gray-400 shrink-0" />
                <div className="flex-1 min-w-0">
                  <p className="text-xs font-semibold text-[#3F3F3F] truncate">{j.uploaded_filename}</p>
                  <p className="text-[10px] text-gray-500">{new Date(j.started_at).toLocaleString("fr-FR")} · {j.started_by}</p>
                </div>
                <span className="text-[10px] text-gray-500 hidden sm:inline">
                  {(j.summary_short?.employees_created || 0)}👥 · {(j.summary_short?.documents_uploaded || 0)}📄
                  {j.summary_short?.documents_failed > 0 && <span className="text-red-500"> · {j.summary_short.documents_failed}⚠</span>}
                </span>
                <Badge className={`rounded-none text-[10px] ${STATUS_COLOR[j.status] || "bg-gray-100"} shrink-0`}>
                  {STATUS_LABEL[j.status] || j.status}
                </Badge>
              </button>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

function Stat({ icon: Icon, label, value, color }) {
  return (
    <div className="border border-gray-100 p-2">
      <div className="flex items-center gap-1.5 text-[10px] uppercase tracking-wider text-gray-500 font-semibold">
        <Icon className={`w-3 h-3 ${color}`} /> {label}
      </div>
      <p className={`text-2xl font-black ${color} leading-none mt-1`}>{value}</p>
    </div>
  );
}
